// ============================================================
// REQUEST VALIDATION — card schedule
// ============================================================
//
// The server is authoritative: every client-originated schedule is
// re-checked here (structure only) before it influences state.

import {
  BATTLE_COLS, BATTLE_ROWS, ELEM, SPELLS,
} from '../constants.ts';
import type { SpellCommand, ValidationResult } from './types.ts';

/**
 * 驗證一份手牌驅動的 3-slot schedule（結構）。
 *   - 元素來自手牌、無積分/等級 gate → 不檢查 powerLevel 範圍。
 *   - 同元素可出現在多個 tick（無跨 tick dedup），只要手上有牌。
 *   - `stackCount`（疊加張數）若提供須為正整數。
 *
 * 仍強制：slot 數 = 3、slotIndex 對位、spellType 為 0(Pass) 或合法 ELEM、
 * targetPos 邊界整數且 positional 法術必填。**手牌持有檢查**（這隊是否真的有
 * 這些牌）是 PvP 的事，由 netserver `RoomBattle.validateOwnership` 對權威 deck
 * 驗；單機信任 client。
 */
export function validateCardSchedule(schedule: SpellCommand[]): ValidationResult {
  if (!Array.isArray(schedule) || schedule.length !== 3) {
    return { valid: false, reason: 'schedule must have 3 slots' };
  }
  for (let i = 0; i < 3; i++) {
    const cmd = schedule[i];
    if (!cmd) return { valid: false, reason: `slot ${i} missing` };
    if (cmd.slotIndex !== i) {
      return { valid: false, reason: `slot ${i} has wrong slotIndex ${cmd.slotIndex}` };
    }
    if (cmd.spellType === 0) continue; // Pass / 不動
    if (cmd.spellType < 1 || cmd.spellType >= ELEM.length) {
      return { valid: false, reason: `slot ${i}: invalid spellType ${cmd.spellType}` };
    }
    if (cmd.stackCount !== undefined) {
      if (!Number.isInteger(cmd.stackCount) || cmd.stackCount < 1) {
        return { valid: false, reason: `slot ${i}: stackCount must be a positive integer` };
      }
    }
    const sp = SPELLS[ELEM[cmd.spellType]];
    if (!sp) return { valid: false, reason: `slot ${i}: unknown spell` };
    if (cmd.targetPos) {
      const { x, y } = cmd.targetPos;
      if (!Number.isInteger(x) || !Number.isInteger(y)) {
        return { valid: false, reason: `slot ${i}: targetPos must have integer coordinates` };
      }
      if (x < 0 || x >= BATTLE_COLS || y < 0 || y >= BATTLE_ROWS) {
        return { valid: false, reason: `slot ${i}: targetPos out of bounds` };
      }
    } else if (sp.effects && sp.effects.length > 0) {
      return { valid: false, reason: `slot ${i}: positional spell requires targetPos` };
    }
  }
  return { valid: true };
}
